'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/lib/stores/auth';
import { isStaff } from '@/lib/staffAccess';
import { Link, useParams } from 'react-router-dom';
import { Avatar } from './ui/Avatar';
import { useNotifications } from '@/contexts/NotificationContext';
import {
  TrendingUp,
  CreditCard,
  FileText,
  Bell,
  Settings,
  Shield,
  LogOut,
} from 'lucide-react';

/** 菜单宽度（w-56 = 14rem = 224px），用于右对齐定位计算。 */
const MENU_WIDTH = 224;

export function UserDropdown() {
  const { user, logout } = useAuthStore();
  const { locale } = useParams<{ locale: string }>();
  const loc = locale || 'en';
  const [open, setOpen] = useState(false);
  // 菜单通过 portal 渲染到 body，脱离 <nav class="glass-navbar"> 的 backdrop-filter
  // 层叠上下文——否则菜单的 backdrop-filter 会被困在导航条层里、够不到真正的页面内容，
  // 导致下方元素透出时「完全清晰无模糊」。fixed 定位 + 按钮矩形算坐标。
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('common');
  const { t: ta } = useTranslation('auth');
  const { unreadCount } = useNotifications();

  const updatePos = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 8, left: r.right - MENU_WIDTH });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePos();
    // outside-click：菜单已 portal 到 body，需同时考察触发按钮与菜单两处。
    function onDown(e: MouseEvent) {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    // 滚动/缩放时重算菜单位置（fixed 跟随）。
    function onLayout() {
      updatePos();
    }
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onLayout, true);
    window.addEventListener('resize', onLayout);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onLayout, true);
      window.removeEventListener('resize', onLayout);
    };
  }, [open, updatePos]);

  if (!user) return null;

  const isAdmin = user.role === 'admin' || isStaff(user);

  const menuItems = [
    { label: t('userMenu.income'), href: `/${loc}/dashboard/provider/income`, icon: TrendingUp },
    { label: t('userMenu.wallet'), href: `/${loc}/dashboard/wallet`, icon: CreditCard },
    { label: t('userMenu.orders'), href: `/${loc}/dashboard/orders`, icon: FileText },
    { label: t('userMenu.notifications'), href: `/${loc}/users/${user.id}/notifications`, icon: Bell, badge: unreadCount },
    { label: t('userMenu.settings'), href: `/${loc}/dashboard/settings`, icon: Settings },
    ...(isAdmin
      ? [{ label: t('userMenu.admin'), href: `/${loc}/dashboard/admin`, icon: Shield }]
      : []),
  ];

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 hover:opacity-80 transition-opacity"
      >
        <Avatar src={user.avatarUrl} alt={user.name} fallback={user.name} size="sm" />
        <span className="text-sm text-foreground max-w-[100px] truncate hidden sm:block">
          {user.name}
        </span>
        <svg
          className={`w-4 h-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: MENU_WIDTH }}
            className="glass-popover rounded-lg py-1 z-50"
          >
            <div className="px-4 py-3 border-b border-border flex items-center gap-3">
              <Avatar src={user.avatarUrl} alt={user.name} fallback={user.name} size="md" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground">
                  {t('userCard.following', { count: user.following ?? 0 })} · {t('userCard.followers', { count: user.followers ?? 0 })}
                </p>
              </div>
            </div>

            <div className="py-1">
              {menuItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-foreground hover:bg-surface-1"
                  onClick={() => setOpen(false)}
                >
                  <item.icon className="w-4 h-4 text-muted-foreground" />
                  {item.label}
                  {item.badge ? (
                    <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-error text-white text-[10px] font-bold">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  ) : null}
                </Link>
              ))}
            </div>

            <div className="border-t border-border">
              <button
                onClick={() => {
                  setOpen(false);
                  logout();
                }}
                className="flex items-center gap-3 w-full text-left px-4 py-2 text-sm text-error hover:bg-error-bg"
              >
                <LogOut className="w-4 h-4" />
                {ta('signOut')}
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
